import React, { useEffect, useState } from "react";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Router } from "./router/Router";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { Routes } from "./pages/Routes";
import { UserDashboard } from "./components/UserDashboard";
import { AuthModal } from "./components/AuthModal";
import { IntroLoader } from "./components/BrandedLoader";
import { AnimatePresence } from "framer-motion";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Main app content with auth
function AppContent() {
  const { user, isAuthenticated, login, logout, loading, showAuthModal, setShowAuthModal } = useAuth();
  const [showDashboard, setShowDashboard] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [introComplete, setIntroComplete] = useState(false);

  // Check if intro was already shown in this session
  useEffect(() => {
    const introShown = sessionStorage.getItem('decx_intro_shown');
    if (introShown) {
      setShowIntro(false);
      setIntroComplete(true);
    }
  }, []);

  // Test backend connection
  // useEffect(() => {
  //   const testBackend = async () => {
  //     try {
  //       const response = await axios.get(`${API}/`);
  //       console.log('Backend connected:', response.data.message);
  //     } catch (e) {
  //       console.error('Backend connection error:', e);
  //     }
  //   };
  //   testBackend();
  // }, []);

  // Show dashboard if user is authenticated and has completed onboarding
  useEffect(() => {
    if (isAuthenticated && user && user.onboarded) {
      setShowDashboard(true);
    }
  }, [isAuthenticated, user]);

  const handleIntroComplete = () => {
    sessionStorage.setItem('decx_intro_shown', 'true');
    setShowIntro(false);
    setTimeout(() => setIntroComplete(true), 500);
  };

  const handleLoginClick = () => {
    if (isAuthenticated && user && user.onboarded) {
      // User already logged in and onboarded, show dashboard
      setShowDashboard(true);
    } else {
      // Show auth modal for login/signup
      setShowAuthModal(true);
    }
  };

  const handleAuthSuccess = (userData, token) => {
    login(userData, token);
    setShowAuthModal(false);
    // Auth modal now handles onboarding, so go directly to dashboard
    setShowDashboard(true);
  };

  const handleLogout = () => {
    logout();
    setShowDashboard(false);
  };

  // Show intro loader first
  if (showIntro) {
    return (
      <AnimatePresence>
        <IntroLoader 
          onComplete={handleIntroComplete} 
          minDuration={3000} 
        />
      </AnimatePresence>
    );
  }

  // Show loading while checking auth (after intro)
  if (loading && introComplete) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#090A0B'
      }}>
        <div style={{ color: '#0082FB', fontSize: '18px' }}>Loading...</div>
      </div>
    );
  }

  // Wait for intro to complete
  if (!introComplete) {
    return null;
  }

  // If dashboard is open, show only the dashboard
  if (showDashboard && user) {
    return (
      <UserDashboard user={user} onLogout={handleLogout} />
    );
  }

  return (
    <Router>
      <div className="App" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Navbar onLoginClick={handleLoginClick} user={user} />
        <main style={{ flex: 1 }}>
          <Routes />
        </main>
        <Footer />
        
        {/* Auth Modal - handles both auth and onboarding */}
        <AuthModal 
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      </div>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
